import { ColumnDef } from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";

import { Checkbox } from "ui/checkbox";
import { Chip } from "ui/chip";
import { Box } from "ui/box";
import { IconButton } from "ui/icon-button";
import CampaignTemplateActionsMenu from "containers/admin/clients/client/campaign-templates-page/campaign-template-actions-menu";
import { getProgramTypeLabel } from "containers/admin/clients/client/campaign-templates-page/campaign-template.constants";
import { useCampaignTemplatesSelection } from "containers/admin/clients/client/campaign-templates-page/campaign-templates-selection-context";

import { formatDateTime } from "utilities/date";

import Icons from "assets";

import type { CampaignTemplate } from "containers/admin/clients/client/campaign-templates-page/campaign-templates-page.types";

const headerStyles = "text-gray-900 font-bold leading-6 tracking-[0.25px] pl-4";
const linkStyles = "text-aqua-600 hover:text-aqua-700";

const SelectAllCampaignTemplatesCheckbox = () => {
  const {
    selectedTemplateIds,
    setSelectedTemplateIds,
    displayedTemplates,
    isCurrentPageAllSelected,
  } = useCampaignTemplatesSelection();

  const handleChange = () => {
    if (isCurrentPageAllSelected) {
      setSelectedTemplateIds((prev) =>
        prev.filter((id) => !displayedTemplates.some((template) => template.id === id)),
      );
    } else {
      setSelectedTemplateIds((prev) => {
        const nextIds = [...prev];

        displayedTemplates.forEach((template) => {
          if (!nextIds.includes(template.id)) {
            nextIds.push(template.id);
          }
        });

        return nextIds;
      });
    }
  };

  return (
    <Checkbox
      size="sm"
      aria-label="Select all campaign briefs"
      checked={isCurrentPageAllSelected}
      indeterminate={
        !isCurrentPageAllSelected &&
        displayedTemplates.some((template) => selectedTemplateIds.includes(template.id))
      }
      onChange={handleChange}
    />
  );
};

const SelectCampaignTemplateCheckbox = ({ templateId }: { templateId: string }) => {
  const { selectedTemplateIds, setSelectedTemplateIds } = useCampaignTemplatesSelection();
  const isSelected = selectedTemplateIds.includes(templateId);

  const handleChange = () => {
    setSelectedTemplateIds((prev) =>
      isSelected ? prev.filter((id) => id !== templateId) : [...prev, templateId],
    );
  };

  return (
    <Checkbox
      size="sm"
      aria-label="Select campaign brief"
      checked={isSelected}
      onChange={handleChange}
    />
  );
};

type CreateCampaignTemplatesTableColumnsParams = {
  onTemplateClick: (templateId: string) => void;
  onArchiveTemplate: (template: CampaignTemplate) => void;
  onGenerateSummary: (templateId: string) => void;
};

export const createCampaignTemplatesTableColumns = ({
  onTemplateClick,
  onArchiveTemplate,
  onGenerateSummary,
}: CreateCampaignTemplatesTableColumnsParams): ColumnDef<CampaignTemplate>[] => [
  {
    id: "checkbox",
    enableSorting: false,
    header: () => <SelectAllCampaignTemplatesCheckbox />,
    cell: ({ row }) => <SelectCampaignTemplateCheckbox templateId={row.original.id} />,
    size: 48,
    meta: {
      className: "p-0 pl-2",
      headerClassName: "p-0 pl-2",
      isFixedWidth: true,
    },
  },
  {
    id: "name",
    header: "Brief name",
    accessorKey: "name",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col justify-center">
        <button
          type="button"
          title={row.original.name}
          className={twMerge("block w-full truncate text-left text-base font-semibold leading-6", linkStyles)}
          onClick={() => onTemplateClick(row.original.id)}>
          {row.original.name}
        </button>
        {row.original.strategy && (
          <a
            href={row.original.strategy}
            target="_blank"
            rel="noreferrer"
            className={twMerge("inline-flex w-fit max-w-full items-center gap-1 truncate text-xs font-semibold leading-4", linkStyles)}>
            View on monday.com <Icons.Forward className="size-3 shrink-0" />
          </a>
        )}
      </div>
    ),
    size: 500,
    meta: {
      className: "py-2 px-3",
      headerClassName: headerStyles,
    },
  },
  {
    id: "program",
    header: "Program type",
    accessorKey: "program",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex justify-center">
        <Chip
          size="small"
          variant="outlined"
          label={getProgramTypeLabel(row.original.program)}
        />
      </div>
    ),
    size: 180,
    meta: {
      headerClassName: twMerge(headerStyles, "whitespace-nowrap text-center"),
      className: "py-2 px-3",
      isHeaderCentered: true,
      isFixedWidth: true,
    },
  },
  {
    id: "lastUpdated",
    header: "Last updated",
    accessorKey: "lastUpdated",
    enableSorting: true,
    cell: ({ row }) => (
      <span className="text-base leading-6 text-gray-600">
        {formatDateTime(row.original.lastUpdated)}
      </span>
    ),
    size: 220,
    meta: {
      headerClassName: headerStyles,
      isFixedWidth: true,
    },
  },
  {
    id: "actions",
    header: "Actions",
    enableSorting: false,
    cell: ({ row }) => (
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
        <IconButton
          variant="text"
          aria-label={`Generate summary for ${row.original.name}`}
          onClick={() => onGenerateSummary(row.original.id)}>
          <Icons.Summary />
        </IconButton>
        <CampaignTemplateActionsMenu
          template={row.original}
          onViewTemplate={onTemplateClick}
          onArchiveTemplate={onArchiveTemplate}
        />
      </Box>
    ),
    size: 112,
    meta: {
      headerClassName: headerStyles,
      className: "py-2 px-3",
      isHeaderCentered: true,
      isPinnedRight: true,
      isFixedWidth: true,
    },
  },
];
